/**
 * Table Data Utilities
 *
 * Purpose: Utility functions for generating dummy advertising data for table demos
 * Architecture: Pure functions for data generation and formatting
 * Dependencies: date-fns for date manipulation
 * Author: Development Team
 * Created: 2025-01-27
 */

// Optimized imports - only import what we actually use
import { format, addDays, subDays } from 'date-fns';

// Advertising-related data interfaces for demo purposes
export interface CampaignData {
  id: string;
  name: string;
  brand: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  budget: number;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpm: number;
  startDate: Date;
  endDate: Date;
  platform: string;
  objective: string;
  targetAudience: string;
  createdBy: string;
  lastModified: Date;
}

export interface MediaPlanData {
  id: string;
  name: string;
  client: string;
  status: 'planning' | 'approved' | 'in-flight' | 'completed';
  totalBudget: number;
  flightDates: string;
  channels: string[];
  targetDemo: string;
  reach: number;
  frequency: number;
  grp: number;
  cpm: number;
  createdBy: string;
  approvedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface VendorData {
  id: string;
  name: string;
  type: 'digital' | 'traditional' | 'ooh' | 'radio' | 'print';
  status: 'active' | 'inactive' | 'pending';
  rating: number;
  totalSpend: number;
  campaigns: number;
  lastActivity: Date;
  contactEmail: string;
  primaryContact: string;
  contractExpiry: Date;
  paymentTerms: string;
  notes: string;
}

// Sample campaign data generator
export const generateCampaignData = (count: number = 50): CampaignData[] => {
  const brands = [
    'Nike',
    'Coca-Cola',
    'Apple',
    'Samsung',
    'Toyota',
    "McDonald's",
    'Adidas',
    'BMW',
    'Google',
    'Microsoft',
  ];
  const platforms = [
    'Facebook',
    'Google Ads',
    'TikTok',
    'Instagram',
    'YouTube',
    'LinkedIn',
    'Twitter',
    'Snapchat',
  ];
  const objectives = [
    'Brand Awareness',
    'Traffic',
    'Conversions',
    'Lead Generation',
    'Video Views',
    'App Installs',
  ];
  const audiences = [
    'Adults 18-34',
    'Adults 25-44',
    'Adults 35-54',
    'Adults 45-65',
    'Millennials',
    'Gen Z',
  ];
  const users = [
    'John Smith',
    'Sarah Johnson',
    'Mike Wilson',
    'Emily Davis',
    'Chris Brown',
    'Lisa Taylor',
  ];
  const statuses: CampaignData['status'][] = ['draft', 'active', 'paused', 'completed'];

  return Array.from({ length: count }, (_, index) => {
    const budget = Math.floor(Math.random() * 100000) + 10000;
    const spend = Math.floor(budget * (Math.random() * 0.8 + 0.1));
    const impressions = Math.floor(Math.random() * 1000000) + 10000;
    const clicks = Math.floor(impressions * (Math.random() * 0.05 + 0.001));
    const ctr = (clicks / impressions) * 100;
    const cpm = spend / (impressions / 1000);

    const startDate = subDays(new Date(), Math.floor(Math.random() * 90));
    const endDate = addDays(startDate, Math.floor(Math.random() * 60) + 7);

    return {
      id: `camp_${(index + 1).toString().padStart(3, '0')}`,
      name: `${brands[Math.floor(Math.random() * brands.length)]} Campaign ${index + 1}`,
      brand: brands[Math.floor(Math.random() * brands.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      budget,
      spend,
      impressions,
      clicks,
      ctr: parseFloat(ctr.toFixed(2)),
      cpm: parseFloat(cpm.toFixed(2)),
      startDate,
      endDate,
      platform: platforms[Math.floor(Math.random() * platforms.length)],
      objective: objectives[Math.floor(Math.random() * objectives.length)],
      targetAudience: audiences[Math.floor(Math.random() * audiences.length)],
      createdBy: users[Math.floor(Math.random() * users.length)],
      lastModified: subDays(new Date(), Math.floor(Math.random() * 7)),
    };
  });
};

// Sample media plan data generator
export const generateMediaPlanData = (count: number = 25): MediaPlanData[] => {
  const clients = [
    'Acme Corp',
    'Global Industries',
    'Tech Solutions',
    'Retail Giant',
    'Healthcare Plus',
  ];
  const channels = ['Digital', 'TV', 'Radio', 'Print', 'OOH', 'Social', 'Video', 'Audio'];
  const demographics = ['Adults 18-34', 'Adults 25-44', 'Adults 35-54', 'Women 25-44', 'Men 18-34'];
  const users = ['John Smith', 'Sarah Johnson', 'Mike Wilson', 'Emily Davis', 'Chris Brown'];
  const statuses: MediaPlanData['status'][] = ['planning', 'approved', 'in-flight', 'completed'];

  return Array.from({ length: count }, (_, index) => {
    const totalBudget = Math.floor(Math.random() * 500000) + 50000;
    const selectedChannels = channels
      .sort(() => 0.5 - Math.random())
      .slice(0, Math.floor(Math.random() * 4) + 1);

    const reach = Math.floor(Math.random() * 80) + 20;
    const frequency = parseFloat((Math.random() * 8 + 1).toFixed(1));
    const grp = Math.floor(reach * frequency);
    const cpm = parseFloat((Math.random() * 15 + 5).toFixed(2));

    const createdAt = subDays(new Date(), Math.floor(Math.random() * 30));
    const updatedAt = addDays(createdAt, Math.floor(Math.random() * 10));

    return {
      id: `plan_${(index + 1).toString().padStart(3, '0')}`,
      name: `${clients[Math.floor(Math.random() * clients.length)]} Media Plan Q${Math.floor(Math.random() * 4) + 1}`,
      client: clients[Math.floor(Math.random() * clients.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      totalBudget,
      flightDates: `${format(addDays(new Date(), 30), 'MM/dd')} - ${format(addDays(new Date(), 90), 'MM/dd')}`,
      channels: selectedChannels,
      targetDemo: demographics[Math.floor(Math.random() * demographics.length)],
      reach,
      frequency,
      grp,
      cpm,
      createdBy: users[Math.floor(Math.random() * users.length)],
      approvedBy: Math.random() > 0.5 ? users[Math.floor(Math.random() * users.length)] : undefined,
      createdAt,
      updatedAt,
    };
  });
};

// Sample vendor data generator
export const generateVendorData = (count: number = 30): VendorData[] => {
  const vendorNames = [
    'MediaCorp Solutions',
    'Digital Dynamics',
    'AdTech Partners',
    'Creative Connect',
    'Brand Builders',
    'Media Masters',
    'Digital Direct',
    'Campaign Central',
    'Ad Agency Plus',
    'Marketing Momentum',
    'Brand Bridge',
    'Media Metrics',
  ];
  const types: VendorData['type'][] = ['digital', 'traditional', 'ooh', 'radio', 'print'];
  const statuses: VendorData['status'][] = ['active', 'inactive', 'pending'];
  const contacts = ['john@vendor.com', 'sarah@media.com', 'mike@agency.com', 'emily@digital.com'];
  const names = ['John Doe', 'Sarah Smith', 'Mike Johnson', 'Emily Brown', 'Chris Wilson'];
  const paymentTerms = ['Net 30', 'Net 60', 'Net 90', 'Immediate', 'Net 15'];

  return Array.from({ length: count }, (_, index) => {
    const totalSpend = Math.floor(Math.random() * 1000000) + 10000;
    const campaigns = Math.floor(Math.random() * 50) + 1;
    const rating = parseFloat((Math.random() * 2 + 3).toFixed(1)); // 3.0 - 5.0

    return {
      id: `vendor_${(index + 1).toString().padStart(3, '0')}`,
      name: vendorNames[Math.floor(Math.random() * vendorNames.length)],
      type: types[Math.floor(Math.random() * types.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      rating,
      totalSpend,
      campaigns,
      lastActivity: subDays(new Date(), Math.floor(Math.random() * 30)),
      contactEmail: contacts[Math.floor(Math.random() * contacts.length)],
      primaryContact: names[Math.floor(Math.random() * names.length)],
      contractExpiry: addDays(new Date(), Math.floor(Math.random() * 365) + 30),
      paymentTerms: paymentTerms[Math.floor(Math.random() * paymentTerms.length)],
      notes: Math.random() > 0.5 ? 'Preferred vendor for digital campaigns' : '',
    };
  });
};

// Utility functions for data formatting
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

export const formatDate = (date: Date): string => {
  return format(date, 'MM/dd/yyyy');
};

export const formatDateTime = (date: Date): string => {
  return format(date, 'MM/dd/yyyy HH:mm');
};
